// M49 (2026-04-25) — Admin AI Scope page.
//
// Cho phép admin BẬT/TẮT từng vùng dữ liệu mà AI (Ask + Chat) được phép truy cập:
//   - Bộ câu hỏi (master toggle + per-bộ checkbox)
//   - Knowledge Base (master toggle)
//   - Nội quy hiện hành (master toggle)
//   - Dặn dò AI prompts (master toggle + per-prompt checkbox)
//
// Trạng thái hiện tại hiển thị rõ ràng: ✅ ĐANG BẬT / ⬜ ĐÃ TẮT
// Mọi thay đổi save ngay (PATCH /admin/ai-scope), không cần nút "Lưu" cuối.
// Áp dụng tức thì cho lần retrieve tiếp theo (không cache cross-request).

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth.js';

// M53 (2026-04-25): label thân thiện thay cho `bo_1000k` khô khan
const BO_LABEL = {
  bo_1000k:      'Bộ 500k',
  bo_1250k:      'Bộ 1000k',
  bo_nang_cao:   'Bộ 1250k',
  bo_ai_da_sua:  'AI đã sửa',
  bo_ai_ngon:    'AI ngon',
};

export default function AdminAiScopePage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'ai_scope' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('ai_scope');
    return false;
  })();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  // Redirect nếu không phải admin
  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin được truy cập trang này');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminAiScope();
      setData(res);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được cấu hình');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function patch(body, successMsg) {
    if (saving) return;
    setSaving(true);
    try {
      await api.adminAiScopeUpdate(body);
      await load();
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !data) {
    return (
      <div className="min-h-full bg-slate-50 py-8 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-6 text-center text-slate-500 text-sm">Đang tải…</div>
        </div>
      </div>
    );
  }

  const { scope, counts, bos, prompts } = data;
  const disabledBosSet = new Set(scope.questions.disabledBos || []);
  const disabledPromptIds = new Set(scope.prompts.disabledIds || []);

  const activeBoCount = bos.filter(b => !disabledBosSet.has(b.bo)).length;
  const activeQuestionCount = bos
    .filter(b => !disabledBosSet.has(b.bo))
    .reduce((s, b) => s + b.embedded_count, 0);

  const activePromptCount = prompts.filter(p => !disabledPromptIds.has(p.id)).length;

  function toggleBo(bo, enabled) {
    const next = new Set(disabledBosSet);
    if (enabled) next.delete(bo); else next.add(bo);
    patch({ questions: { disabledBos: Array.from(next) } }, enabled ? `Đã bật bộ ${bo}` : `Đã tắt bộ ${bo}`);
  }

  function togglePrompt(promptId, enabled) {
    const next = new Set(disabledPromptIds);
    if (enabled) next.delete(promptId); else next.add(promptId);
    patch({ prompts: { disabledIds: Array.from(next) } }, enabled ? `Đã bật prompt #${promptId}` : `Đã tắt prompt #${promptId}`);
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-sky-50/60 via-white to-indigo-50/40 py-8 px-4 md:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back */}
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 inline-block mb-4">← Trang admin</Link>

        {/* Header */}
        <header className="mb-6 bg-white rounded-2xl border border-sky-100 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white flex items-center justify-center text-2xl shadow-md">
            🎯
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-slate-800">Phạm vi AI truy cập</h1>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Bật/tắt các vùng dữ liệu mà AI được phép dùng làm ngữ cảnh trả lời.
              Áp dụng ngay cho cả <b>Hỏi đáp</b> và <b>Chat AI</b>.
            </p>
          </div>
        </header>

        {/* Info banner */}
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <div className="font-semibold mb-1">💡 Lưu ý</div>
          <ul className="list-disc ml-5 text-xs leading-relaxed space-y-0.5">
            <li>Tắt vùng <b>không xoá data</b> — chỉ exclude khỏi retrieve. Bật lại dùng ngay.</li>
            <li>Tắt nguồn duy nhất chứa đáp án → AI có thể trả lời "không đủ thông tin".</li>
            <li>Thay đổi lưu ngay, không cần nút "Lưu".</li>
          </ul>
        </div>

        {/* ===== CARD 1: Bộ câu hỏi ===== */}
        <SourceCard
          icon="📋"
          title="Bộ câu hỏi"
          description={`${counts.questions.total_embedded}/${counts.questions.total_questions} câu đã embed · ${bos.length} bộ AI đọc được${counts.questions.total_bos < bos.length ? ` (${counts.questions.total_bos} bộ có câu)` : ''}`}
          enabled={scope.questions.enabled}
          onToggle={(v) => patch({ questions: { enabled: v } }, v ? 'Đã bật nguồn Câu hỏi' : 'Đã tắt nguồn Câu hỏi')}
          subStat={scope.questions.enabled ? `Đang dùng: ${activeBoCount}/${bos.length} bộ · ${activeQuestionCount} câu` : 'Toàn bộ đang tắt'}
          disabled={saving}
        >
          {scope.questions.enabled && bos.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-600">Chi tiết từng bộ:</div>
                <div className="text-[10px] text-slate-400">AI đọc được {bos.length} bộ</div>
              </div>
              {bos.map(b => {
                const isOn = !disabledBosSet.has(b.bo);
                const isEmpty = b.count === 0;
                return (
                  <CheckboxRow
                    key={b.bo}
                    checked={isOn}
                    onChange={(v) => toggleBo(b.bo, v)}
                    disabled={saving || isEmpty}
                    label={
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-700">{BO_LABEL[b.bo] || b.bo}</span>
                        <span className="font-mono text-[10px] text-slate-400">{b.bo}</span>
                        {isEmpty && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                            TRỐNG
                          </span>
                        )}
                      </span>
                    }
                    stat={isEmpty ? 'Chưa có câu — admin thêm vào mới bật được' : `${b.embedded_count}/${b.count} câu đã embed`}
                  />
                );
              })}
              {/* M53 note: giải thích tại sao không có "Chờ duyệt" */}
              <div className="mt-3 pt-2 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
                <span className="font-semibold">💡 Lưu ý:</span> Bộ <b>"Chờ duyệt"</b> (câu AI/NV gợi ý đang đợi admin phê duyệt) <b>KHÔNG</b> nằm trong danh sách — AI mặc định không bao giờ đọc bộ này để tránh đưa câu sai cho NV khác.
              </div>
            </div>
          )}
        </SourceCard>

        {/* ===== CARD 2: Knowledge Base ===== */}
        <SourceCard
          icon="📚"
          title="Kiến thức (Knowledge Base)"
          description={`${counts.knowledge.total_kb} tài liệu · ${counts.knowledge.total_chunks} chunks đã embed`}
          enabled={scope.knowledge.enabled}
          onToggle={(v) => patch({ knowledge: { enabled: v } }, v ? 'Đã bật KB' : 'Đã tắt KB')}
          subStat={scope.knowledge.enabled ? 'Áp dụng cho cả Hỏi đáp + Chat AI' : 'Ask + Chat AI sẽ KHÔNG dùng KB'}
          disabled={saving}
        />

        {/* ===== CARD 3: Nội quy ===== */}
        <SourceCard
          icon="📜"
          title="Nội quy (hiện hành)"
          description={counts.rules.current_version
            ? `Phiên bản ${counts.rules.current_version} · ${counts.rules.total_modules} chương · ${counts.rules.total_chunks} chunks đã embed`
            : 'Chưa có phiên bản nội quy hiện hành'}
          enabled={scope.rules.enabled}
          onToggle={(v) => patch({ rules: { enabled: v } }, v ? 'Đã bật Nội quy' : 'Đã tắt Nội quy')}
          subStat={scope.rules.enabled ? 'Chỉ áp dụng cho Hỏi đáp (Chat AI không dùng nội quy)' : 'Hỏi đáp sẽ KHÔNG dùng nội quy làm ngữ cảnh'}
          disabled={saving}
        />

        {/* ===== CARD 4: Dặn dò AI ===== */}
        <SourceCard
          icon="📢"
          title="Dặn dò AI (prompts)"
          description={`${counts.prompts.total_active} prompts active`}
          enabled={scope.prompts.enabled}
          onToggle={(v) => patch({ prompts: { enabled: v } }, v ? 'Đã bật Dặn dò AI' : 'Đã tắt Dặn dò AI')}
          subStat={scope.prompts.enabled
            ? `Đang dùng: ${activePromptCount}/${prompts.length} prompts`
            : 'Tất cả prompts đang tắt'
          }
          disabled={saving}
        >
          {scope.prompts.enabled && prompts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              <div className="text-xs font-semibold text-slate-600 mb-2">Chi tiết từng prompt:</div>
              {prompts.map(p => {
                const isOn = !disabledPromptIds.has(p.id);
                return (
                  <CheckboxRow
                    key={p.id}
                    checked={isOn}
                    onChange={(v) => togglePrompt(p.id, v)}
                    disabled={saving}
                    label={
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-700">{p.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-wide">{p.scope}</span>
                        {p.is_default ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">DEFAULT</span> : null}
                      </span>
                    }
                    stat={<span className="text-[11px] text-slate-400 line-clamp-1">{p.preview || ''}</span>}
                  />
                );
              })}
            </div>
          )}
        </SourceCard>

        {/* Footer note */}
        <div className="mt-6 text-center text-xs text-slate-400">
          Thay đổi đã lưu sẽ áp dụng ngay cho lần Hỏi đáp / Chat tiếp theo.
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function SourceCard({ icon, title, description, enabled, onToggle, subStat, disabled, children }) {
  return (
    <div className={`mb-4 bg-white rounded-2xl border ${enabled ? 'border-emerald-200 shadow-sm' : 'border-slate-200'} p-5 transition`}>
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${enabled ? 'bg-emerald-50' : 'bg-slate-100 grayscale opacity-60'}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-800">{title}</h2>
                <StatusBadge enabled={enabled} />
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{description}</p>
            </div>
            <ToggleSwitch enabled={enabled} onChange={onToggle} disabled={disabled} />
          </div>
          {subStat && (
            <div className={`mt-2 text-xs ${enabled ? 'text-slate-600' : 'text-rose-600'}`}>
              {enabled ? '→ ' : '⚠ '}{subStat}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function StatusBadge({ enabled }) {
  if (enabled) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
        ✅ Đang bật
      </span>
    );
  }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-200 text-slate-600">
      ⬜ Đã tắt
    </span>
  );
}

function ToggleSwitch({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 ${
        enabled ? 'bg-emerald-500' : 'bg-slate-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function CheckboxRow({ checked, onChange, disabled, label, stat }) {
  return (
    <label className={`flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded text-emerald-500 focus:ring-emerald-400 accent-emerald-500"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-800">{label}</div>
        {stat && <div className="text-xs text-slate-500 mt-0.5">{stat}</div>}
      </div>
      {checked
        ? <span className="text-[10px] font-bold text-emerald-600 shrink-0">✓ BẬT</span>
        : <span className="text-[10px] font-bold text-slate-400 shrink-0">TẮT</span>
      }
    </label>
  );
}
